/**
 * Per-tenant school access-control flags (SchoolService — `/AccessSettings`).
 *
 * The SchoolService serializes PascalCase across the board, so these DTOs are
 * PascalCase too — 1:1 with the wire format, no mappers. `GET` returns the
 * tenant's flags (creating the row with service defaults on first read);
 * `PUT` has patch semantics — only the provided fields are updated — and
 * echoes the full updated record.
 */

/** GET/PUT `/AccessSettings` response. */
export interface SchoolAccessSettings {
  AccessSettingsId: string;
  /** Gate operational flag: log the access only after the physical gate turn. */
  RegisterAccessLogAfterAccessGatePassage: boolean;
  /** Gate operational flag: show passages on the Live Pickup panel (opt-in). */
  UseLivePickupPanel: boolean;
  /**
   * Gate operational flag: identify the person by their access code typed on
   * the gate keypad, instead of the person code.
   */
  UseCodeIdentifier: boolean;
  /** Gate operational flag: block typing on the gate keypad altogether. */
  BlockKeyboardCode: boolean;
  /** Per-rule switch: block entry without an active enrollment. */
  EnforceActiveEnrollment: boolean;
  /** Per-rule switch: block entry on non-school days. */
  EnforceSchoolDay: boolean;
  /** Per-rule switch: block entry outside class hours. */
  EnforceSchedule: boolean;
  /**
   * Per-rule switch: also honour each lesson's own gate markers (038).
   * Subordinate to `EnforceSchedule` — with that one off this is inert, and the
   * service accepts the combination without complaining.
   */
  EnforceLessonSchedule: boolean;
  /**
   * Per-rule switch: block a student whose accession is not up to date. The
   * only rule switch that defaults to `false` — accession is a commercial
   * choice, so a tenant that never touched it must keep the gate open. It
   * applies to students only; staff and guardians are never checked.
   */
  EnforceAccession: boolean;
  /**
   * Per-rule switch: treat the guardian tie as an access axis (040). With it on,
   * a person holding an active guardian tie is judged at the gate by that tie's
   * pickup authorization — the refusal texts are the three `Pickup*` messages in
   * `SchoolMessageSettings`. Opt-in and independent: it defaults to `false` and
   * depends on no other switch.
   */
  EnforceGuardianAsAxis: boolean;
  EnforceGuardianForExit: boolean;
}

/** PUT `/AccessSettings` body (patch semantics — omitted fields are kept). */
export type SchoolAccessSettingsRequest = Partial<SchoolAccessSettings> &
  Pick<SchoolAccessSettings, 'AccessSettingsId'>;

export interface SchoolAccessSettingsState {
  /** Flags from the last successful load/save (null until loaded). */
  accessSettings: SchoolAccessSettings | null;
}

export const initialSchoolAccessSettingsState: SchoolAccessSettingsState = {
  accessSettings: null,
};
